import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { openModal } from "store/modals/slice";
import { ModalTypes } from "configs/types";
import { getRiskLimitsList, getRiskLimitItem } from "store/risk/limits/slice";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import { useTranslation } from "react-i18next";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";
import { usePermissionHelper } from "helpers/usePermissionHelper";

const LimitsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const permissions = usePermissionHelper();
  const data = useAppSelector((store) => store.riskLimits.list);
  const processingItemId = useAppSelector((store) => store.riskLimits.processingItemId);

  const isListFetched = useAppSelector(
    (store) => store.riskLimits.isListFetched,
  );
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'riskLimits',
    isListFetched
  });

  useEffect(() => {
    if (isListFetched) return;

    dispatch(getRiskLimitsList());
  }, [isListFetched]);

  const renderRiskLevel = (level: string) => {
    return <span className={`badge badge--${level}`}>{level}</span>;
  };

  const handleOpenEditModal = (id: number) => {
    const row = data.find((item) => item.id === id);

    if (!row) return;

    dispatch(
      openModal({
        activeModal: ModalTypes.riskLimit,
      }),
    );

    navigate(
      `${location.pathname}?activeModal=${ModalTypes.riskLimit}&riskLevel=${row.riskLevel}&operationType=${row.operationType}&currency=${row.currency}`,
    );

    dispatch(
      getRiskLimitItem({
        riskLevel: row.riskLevel,
        operationType: row.operationType,
        currency: row.currency,
      }),
    );
  };

  const actionProps = permissions?.update
    ? {
        onEdit: (id: number) => handleOpenEditModal(id),
        onView: undefined,
      }
    : {
        onEdit: undefined,
        onView: (id: number) => handleOpenEditModal(id),
      };

  return (
    <div className="page-list-wrapper">
      <div className="page-list-header">
        <div className="page-list-header--box"></div>

        <div className="page-list-header--box page-list-header--box--right">
          <RefreshButton
            position="right"
            disabled={isRefreshRequested}
            isLoading={isRefreshRequested}
            onClick={() => refresh()}
          />
        </div>
      </div>

      {!isListFetched && <DataTableSkeleton columnsCount={7} rowsCount={12} />}

      {isListFetched && (
        <div className="game-table-wrap table">
          {data.length === 0 && <EmptyState message={t("noRiskLimitsFound")} />}

          {data.length > 0 && (
            <Table
              data={data}
              idField="id"
              hasCheckbox={false}
              hasOptions={permissions?.read || permissions?.update}
              hasDelete={false}
              hasEdit={permissions?.update}
              hasView={permissions?.read && !permissions?.update}
              processingId={processingItemId}
              {...actionProps}
              columns={{
                hiddenColumns: ["id"],
              }}
              customColumns={(row) => ({
                riskLevel: renderRiskLevel(row.riskLevel),
              })}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default LimitsList;
